'use client';

import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { supabase } from '@/lib/supabase';
import { Loader2 } from 'lucide-react';
import type { Timeframe, SportFilter, BetTypeFilter } from './ReportingFilters';

interface SportData {
  sport: string;
  units: number;
  roi: number;
  record: string;
  winPct: number;
}

interface SportBreakdownChartProps {
  timeframe: Timeframe;
  sport: SportFilter;
  betType: BetTypeFilter;
}

function CustomTooltip({ active, payload, label, sportData }: { active?: boolean; payload?: Array<{value: number}>; label?: string; sportData: SportData[] }) {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  const sport = sportData.find((s) => s.sport === label);
  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-xl px-4 py-3 shadow-lg">
      <p className="text-xs font-semibold text-[hsl(var(--foreground))] mb-2">{label}</p>
      <p className={`font-mono font-bold text-lg tabular-nums ${val >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
        {val >= 0 ? '+' : ''}{val.toFixed(1)}u
      </p>
      {sport && (
        <div className="mt-2 space-y-0.5">
          <p className="text-xs text-[hsl(var(--foreground-muted))]">Record: {sport.record}</p>
          <p className="text-xs text-[hsl(var(--foreground-muted))]">Win%: {sport.winPct}%</p>
          <p className="text-xs text-[hsl(var(--foreground-muted))]">ROI: {sport.roi >= 0 ? '+' : ''}{sport.roi}%</p>
        </div>
      )}
    </div>
  );
}

function getDateFilter(timeframe: Timeframe): string | null {
  const now = new Date();
  if (timeframe === '7d') { now.setDate(now.getDate() - 7); return now.toISOString(); }
  if (timeframe === '30d') { now.setDate(now.getDate() - 30); return now.toISOString(); }
  if (timeframe === '90d') { now.setDate(now.getDate() - 90); return now.toISOString(); }
  if (timeframe === 'ytd') { return new Date(now.getFullYear(), 0, 1).toISOString(); }
  return null;
}

export default function SportBreakdownChart({ timeframe, sport, betType }: SportBreakdownChartProps) {
  const [sportData, setSportData] = useState<SportData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        let query = supabase
          .from('picks')
          .select('sport, result, units, units_result, posted_at')
          .neq('result', 'pending');

        if (sport !== 'all') query = query.eq('sport', sport);
        if (betType !== 'all') query = query.eq('bet_type', betType);
        const dateFrom = getDateFilter(timeframe);
        if (dateFrom) query = query.gte('posted_at', dateFrom);

        const { data, error } = await query;
        if (error) throw error;

        const bySport: Record<string, { units: number; risked: number; wins: number; losses: number; pushes: number }> = {};
        (data || []).forEach((p) => {
          if (!bySport[p.sport]) bySport[p.sport] = { units: 0, risked: 0, wins: 0, losses: 0, pushes: 0 };
          bySport[p.sport].units += p.units_result || 0;
          bySport[p.sport].risked += p.units || 0;
          if (p.result === 'win') bySport[p.sport].wins++;
          else if (p.result === 'loss') bySport[p.sport].losses++;
          else if (p.result === 'push') bySport[p.sport].pushes++;
        });

        const stats: SportData[] = Object.entries(bySport).map(([s, d]) => {
          const winPct = d.wins + d.losses > 0 ? parseFloat(((d.wins / (d.wins + d.losses)) * 100).toFixed(1)) : 0;
          const roi = d.risked > 0 ? parseFloat(((d.units / d.risked) * 100).toFixed(1)) : 0;
          return {
            sport: s,
            units: parseFloat(d.units.toFixed(1)),
            roi,
            winPct,
            record: `${d.wins}-${d.losses}${d.pushes > 0 ? `-${d.pushes}` : ''}`,
          };
        });

        setSportData(stats.sort((a, b) => b.units - a.units));
      } catch {
        setSportData([]);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [timeframe, sport, betType]);

  return (
    <div className="stat-card mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-[hsl(var(--foreground))]">Units by Sport</h2>
          <p className="text-xs text-[hsl(var(--foreground-muted))] mt-0.5">Net units won per sport</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[220px] gap-2">
          <Loader2 size={18} className="animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm text-[hsl(var(--foreground-muted))]">Loading chart...</span>
        </div>
      ) : sportData.length === 0 ? (
        <div className="flex items-center justify-center h-[220px]">
          <p className="text-sm text-[hsl(var(--foreground-muted))]">No data yet. Chart will appear once picks are graded.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={sportData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 13%, 90%)" vertical={false} />
            <XAxis dataKey="sport" tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'DM Sans' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'hsl(220, 10%, 50%)', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}u`} />
            <Tooltip content={(props) => <CustomTooltip {...props} sportData={sportData} />} />
            <Bar dataKey="units" radius={[6, 6, 0, 0]}>
              {sportData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.units >= 0 ? 'hsl(155, 65%, 38%)' : 'hsl(0, 72%, 52%)'} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}